<template>
  <section class="community-copy" :style="copyStyle" aria-labelledby="community-copy-title">
    <h2 id="community-copy-title" class="community-copy__title" :style="boxStyle({ x: 323, y: 0, w: 1274, h: 294 })">
      {{ content.title }}
    </h2>
    <p
      v-if="content.subtitle"
      class="community-copy__subtitle"
      :style="boxStyle({ x: 418, y: 344, w: 1085, h: 280 })"
    >
      {{ content.subtitle }}
    </p>
    <p
      v-if="content.description"
      class="community-copy__description"
      :style="boxStyle({ x: 418, y: 604, w: 1085, h: 84 })"
    >
      {{ content.description }}
    </p>
    <span
      id="news"
      class="community-copy__news-anchor"
      :style="boxStyle({ x: 0, y: 548, w: 1, h: 1 })"
      aria-hidden="true"
    />
    <a
      v-for="card in pressCards"
      :key="card.name"
      class="community-copy__press-card"
      :style="boxStyle(card.box)"
      :href="card.href"
      :aria-label="card.name"
      target="_blank"
      rel="noopener noreferrer"
    >
      <img
        :src="card.image"
        :alt="card.alt"
        loading="lazy"
        decoding="async"
        draggable="false"
      />
    </a>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { CSSProperties } from 'vue'

import type { DesignBox, PressCard } from '../../data/home2026'

const props = defineProps<{
  content: {
    title: string
    subtitle: string
    description: string
  }
  pressCards: PressCard[]
  scaledPx: (value: number) => string
}>()

const copyStyle = computed<CSSProperties>(() => ({
  height: props.scaledPx(1658),
  '--community-title-font': props.scaledPx(70),
  '--community-title-line': props.scaledPx(98),
  '--community-subtitle-font': props.scaledPx(50),
  '--community-subtitle-line': props.scaledPx(70),
  '--community-description-font': props.scaledPx(30),
  '--community-description-line': props.scaledPx(42),
  '--community-card-radius': props.scaledPx(12),
}))

const boxStyle = (box: DesignBox): CSSProperties => ({
  left: props.scaledPx(box.x),
  top: props.scaledPx(box.y),
  width: props.scaledPx(box.w),
  height: props.scaledPx(box.h),
})
</script>

<style scoped>
.community-copy {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  background: #000;
  color: #fff;
  font-family: 'Alibaba PuHuiTi 3.0', AlibabaPuHuiTi_3_55_Regular, PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-synthesis: none;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricprecision;
}

.community-copy__title,
.community-copy__subtitle,
.community-copy__description,
.community-copy__news-anchor,
.community-copy__press-card {
  position: absolute;
  box-sizing: border-box;
}

.community-copy__news-anchor {
  pointer-events: none;
}

.community-copy__title,
.community-copy__subtitle,
.community-copy__description {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  letter-spacing: 0;
  text-align: center;
}

.community-copy__title {
  font-family: AlibabaPuHuiTi_3_115_Black, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--community-title-font);
  font-weight: 1000;
  line-height: var(--community-title-line);
}

.community-copy__subtitle {
  margin-top: var(--community-subtitle-top);
  font-family: AlibabaPuHuiTi_3_115_Black, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--community-subtitle-font);
  font-weight: 1000;
  line-height: var(--community-subtitle-line);
}

.community-copy__description {
  color: #fff;
  font-family: AlibabaPuHuiTi_3_55_Regular, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--community-description-font);
  font-weight: 400;
  line-height: var(--community-description-line);
}

.community-copy__press-card {
  display: block;
  overflow: hidden;
  border-radius: var(--community-card-radius);
  color: inherit;
  text-decoration: none;
  transition: transform 180ms ease;
}

.community-copy__press-card:hover,
.community-copy__press-card:focus-visible {
  transform: translateY(-2px);
}

.community-copy__press-card:focus-visible {
  outline: var(--focus-outline-width) solid rgb(138 188 249 / 90%);
  outline-offset: var(--focus-outline-offset);
}

.community-copy__press-card img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
  user-select: none;
}
</style>
